import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { useScrollReveal } from '../hooks/useScrollReveal.js';
import { useNavTone } from '../hooks/useNavTone.js';
import { appUrl, openPlayStore } from '../lib/appUrl.js';
import './SpaceOwners.css';

const MOBILE_LINKS = [
  { href: '/space-owners#value', label: 'What you get' },
  { href: '/space-owners#dashboard', label: 'Host dashboard' },
  { href: '/space-owners#escrow', label: 'Escrow & payments' },
  { href: '/space-owners#faq', label: 'FAQ' },
];

const NAV_SECTIONS = [
  ['hero', 'dark'],
  ['problem', 'light'],
  ['listing-steps', 'light'],
  ['value', 'light'],
  ['dashboard', 'dark'],
  ['escrow', 'dark'],
  ['faq', 'light'],
  ['cta', 'dark'],
];

const CAL_DAYS = [
  { cls: 'hdr', label: 'M' }, { cls: 'hdr', label: 'T' }, { cls: 'hdr', label: 'W' }, { cls: 'hdr', label: 'T' }, { cls: 'hdr', label: 'F' }, { cls: 'hdr', label: 'S' }, { cls: 'hdr', label: 'S' },
  { cls: '', label: '' }, { cls: '', label: '' }, { cls: '', label: '' }, { cls: 'avail', label: '1' }, { cls: 'avail', label: '2' }, { cls: 'avail', label: '3' }, { cls: 'avail', label: '4' },
  { cls: 'bkd', label: '5' }, { cls: 'bkd', label: '6' }, { cls: 'bkd', label: '7' }, { cls: 'bkd', label: '8' }, { cls: 'bkd', label: '9' }, { cls: 'bkd', label: '10' }, { cls: 'bkd', label: '11' },
  { cls: 'avail', label: '12' }, { cls: 'avail', label: '13' }, { cls: 'pend', label: '14' }, { cls: 'pend', label: '15' }, { cls: 'pend', label: '16' }, { cls: 'avail', label: '17' }, { cls: 'avail', label: '18' },
  { cls: 'bkd', label: '19' }, { cls: 'bkd', label: '20' }, { cls: 'bkd', label: '21' }, { cls: 'bkd', label: '22' }, { cls: 'bkd', label: '23' }, { cls: 'bkd', label: '24' }, { cls: 'bkd', label: '25' },
  { cls: 'avail', label: '26' }, { cls: 'avail', label: '27' }, { cls: 'avail', label: '28' }, { cls: 'avail', label: '29' }, { cls: 'avail', label: '30' }, { cls: '', label: '' }, { cls: '', label: '' },
];

const FAQS = [
  {
    q: 'Who can list a space on FlipAd?',
    a: 'Any individual or business that owns or manages OOH advertising space in India can list on FlipAd — from a single billboard in a Tier-2 city to a network of transit panels across metros. You need to hold the valid permit or lease rights for the site.',
  },
  {
    q: 'What formats does FlipAd support?',
    a: "Billboards, unipoles, digital screens, transit panels, bus shelters, retail kiosks, and commercial wall spaces. If it's OOH and you have the rights to it, it can be listed.",
  },
  {
    q: 'How does payment work?',
    a: 'When an advertiser confirms a booking, their payment is held in FlipAd escrow. Once your campaign runs and you submit verified proof of display, the funds are released to your linked bank account within 2–3 business days.',
  },
  {
    q: 'What is proof of display and how do I submit it?',
    a: "Proof of display is a timestamped photo of your site showing the advertiser's creative running. You upload it through your host dashboard at the end of the campaign period. FlipAd reviews and verifies it before releasing payment.",
  },
  {
    q: 'Is there a fee to list my space?',
    a: 'Listing is completely free. FlipAd charges a small platform fee only when a booking successfully completes — deducted from the campaign amount before the balance is released to you.',
  },
  {
    q: 'How long does it take to get my first booking?',
    a: 'It depends on your location, format, and pricing. Spaces in high-traffic urban areas with complete listings — good photos, clear dimensions, and transparent pricing — typically receive their first inquiry within two weeks of listing.',
  },
];

function ProblemCard({ dataD, ariaLabel, tag, title, desc, children }) {
  return (
    <div className="so-prob-card reveal" data-d={dataD} role="group" aria-label={ariaLabel}>
      <div className="so-prob-illus">{children}</div>
      <div className="so-prob-body">
        <p className="so-prob-tag"><i className="ph-fill ph-warning-circle" aria-hidden="true" />{tag}</p>
        <h3 className="so-prob-title">{title}</h3>
        <p className="so-prob-desc">{desc}</p>
      </div>
    </div>
  );
}

function StepCard({ dataD, num, title, desc }) {
  return (
    <div className="so-step-card reveal" data-d={dataD}>
      <div className="so-step-num">{num}</div>
      <div className="so-step-title">{title}</div>
      <p className="so-step-desc">{desc}</p>
    </div>
  );
}

function ValueCard({ dataD, icon, title, desc }) {
  return (
    <div className="so-value-card reveal" data-d={dataD}>
      <div className="so-value-icon"><i className={`ph ${icon}`} aria-hidden="true" /></div>
      <div className="so-value-title">{title}</div>
      <p className="so-value-desc">{desc}</p>
    </div>
  );
}

function SpaceRow({ thumbBg, name, sub, badge, badgeLabel, rate }) {
  return (
    <div className="so-db-space-row">
      <div className="so-db-space-thumb" style={{ background: thumbBg }} />
      <div className="so-db-space-info">
        <div className="so-db-space-name">{name}</div>
        <div className="so-db-space-sub">{sub}</div>
      </div>
      <div className={`so-db-badge ${badge}`}><i className="ph-fill ph-circle" style={{ fontSize: '6px' }} /> {badgeLabel}</div>
      <div className="so-db-space-rate">{rate}</div>
    </div>
  );
}

function EscrowStep({ n, icon, title, sub, isEscrow }) {
  return (
    <div className={`esc-stp esc-stp-${n}${isEscrow ? ' is-escrow' : ''}`}>
      <div className="esc-stp-icon"><i className={`ph ${icon}`} aria-hidden="true" /></div>
      <div className="esc-stp-title">{title}</div>
      <div className="esc-stp-sub">{sub}</div>
    </div>
  );
}

function EscrowConnector({ n, color, label }) {
  return (
    <div className={`esc-conn esc-conn-${n} ${color}`} aria-hidden="true">
      <div className="esc-conn-track"><div className="esc-conn-line" /><div className="esc-conn-tip" /></div>
      <div className="esc-conn-lbl">{label}</div>
    </div>
  );
}

function FaqItem({ q, a, isOpen, onToggle }) {
  return (
    <div className={`so-faq-item${isOpen ? ' is-open' : ''}`}>
      <button className="so-faq-q" type="button" aria-expanded={isOpen} onClick={onToggle}>
        {q}
        <i className="ph ph-plus so-faq-icon" aria-hidden="true" />
      </button>
      <div className="so-faq-a">
        <div className="so-faq-a-inner">{a}</div>
      </div>
    </div>
  );
}

export default function SpaceOwners() {
  useScrollReveal();
  useNavTone(NAV_SECTIONS);

  // Mirrors the original's onmouseover/onmouseout inline handlers on the
  // "Looking to advertise?" hero link, which swap its color on hover.
  const [advLinkHover, setAdvLinkHover] = useState(false);

  // Mirrors the original's FAQ accordion script: clicking a question closes
  // any other open item first, so at most one item is open at a time.
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Mirrors the original's escrow-diagram IntersectionObserver: adds
  // "esc-active" once the diagram scrolls into view, which kicks off the
  // step-by-step CSS draw animation defined in SpaceOwners.css.
  //
  // Toggled via direct classList mutation, not React state — useScrollReveal
  // (below) also mutates this same element's classList imperatively to add
  // "in" once it's visible, entirely outside React's render cycle. If this
  // were state-driven, the className the JSX renders (a template string with
  // no "in" in it) would win on the next re-render triggered by this state
  // change, since React's DOM diff replaces the whole className attribute —
  // silently wiping out "in" and leaving the diagram permanently at
  // opacity:0 (confirmed live: the observer fired and called classList.add,
  // but a later escActive-driven re-render clobbered it moments later).
  const escDiagRef = useRef(null);

  useEffect(() => {
    const el = escDiagRef.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) {
      el.classList.add('esc-active');
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { el.classList.add('esc-active'); obs.disconnect(); }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="For Space Owners — List Your Ad Space & Earn | FlipAd"
        description="List your billboard, unipole, or digital screen for free and start earning. Manage bookings from a dashboard, get paid via escrow, and submit proof of display in minutes."
        path="/space-owners"
      />
      <Header mobileLinks={MOBILE_LINKS} activeNavKey="space-owners" />

      {/* ════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════ */}
      <section className="so-hero" id="hero" aria-labelledby="so-hero-hl">
        <div className="container">
          <div className="so-hero-inner">

            <div className="so-hero-left">
              <p className="eyebrow">For Space Owners</p>
              <h1 className="so-hero-hl" id="so-hero-hl">
                Your space, listed.<br />Your payment, guaranteed.
              </h1>
              <p className="so-hero-sub">
                FlipAd connects your OOH inventory with verified advertisers across India.
                Free to list. Escrow-protected earnings. No chasing payments.
              </p>
              <div className="so-hero-ctas">
                <a href={appUrl("/auth")} target="_blank" rel="noopener noreferrer" className="lmn-btn primary lg">
                  List your space — it&apos;s free <i className="ph ph-arrow-right" aria-hidden="true" />
                </a>
                {/* Not a plain <a href="#listing-steps">: under HashRouter that
                    native hash jump would overwrite the router's own URL
                    fragment, changing the "route" to /listing-steps (no such
                    route exists) instead of just scrolling within this page. */}
                <Link to="/space-owners#listing-steps" className="lmn-btn outline-white lg">
                  See how it works ↓
                </Link>
              </div>
              {/* Cross-app navigation via a real browser nav (not react-router's
                  navigate(), which can't cross into the main app's separate
                  BrowserRouter/bundle) — replace so the click itself leaves no
                  back-button entry. */}
              <button
                type="button"
                style={{
                  display: 'inline-block',
                  marginTop: 'var(--lumen-space-4)',
                  fontSize: 'var(--lumen-text-sm)',
                  fontWeight: 600,
                  color: advLinkHover ? 'rgba(255,255,255,0.75)' : 'color-mix(in srgb,#fff 45%,transparent)',
                  textDecoration: 'none',
                  transition: 'color 150ms ease',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setAdvLinkHover(true)}
                onMouseLeave={() => setAdvLinkHover(false)}
                onClick={openPlayStore}
              >
                Looking to advertise? Browse inventory →
              </button>
            </div>

            <div className="so-hero-right" aria-hidden="true">
              <div className="so-hero-frame">
                <div className="so-hero-frame-bar">
                  <div className="sol-frame-dot" />
                  <div className="sol-frame-dot" />
                  <div className="sol-frame-dot" />
                  <div className="sol-frame-url">flipad.in/host/dashboard</div>
                </div>
                <div className="so-hero-dash-body">
                  <div className="so-dash-topbar">
                    <div>
                      <div className="so-dash-title">Host Dashboard</div>
                      <div className="so-dash-period">June 2026</div>
                    </div>
                    <div className="so-db-add" style={{ fontSize: '11px', height: '26px', padding: '0 10px', gap: '4px', borderRadius: '6px' }}>
                      <i className="ph ph-plus" style={{ fontSize: '10px' }} /> Add space
                    </div>
                  </div>

                  <div className="so-stat-row">
                    <div className="si-tile">
                      <div className="si-tile-val">4</div>
                      <div className="si-tile-lbl">Active bookings</div>
                    </div>
                    <div className="si-tile">
                      <div className="si-tile-val" style={{ fontSize: '13px' }}>₹1.24L</div>
                      <div className="si-tile-lbl">This month</div>
                    </div>
                    <div className="si-tile">
                      <div className="si-tile-val" style={{ color: 'var(--lumen-amber-500)' }}>2</div>
                      <div className="si-tile-lbl">Pending</div>
                    </div>
                  </div>

                  <div className="host-payout">
                    <div className="host-payout-label">
                      <i className="ph-fill ph-check-circle" style={{ fontSize: '13px', color: 'var(--lumen-emerald-500)' }} />
                      Payment released · MG Road Billboard
                    </div>
                    <div className="host-payout-amt">+₹38,000</div>
                  </div>

                  <div className="si-cal">
                    <div className="si-cal-hd">June 2026</div>
                    <div className="si-cal-grid">
                      {CAL_DAYS.map((d, i) => (
                        <div key={i} className={`si-day${d.cls ? ` ${d.cls}` : ''}`}>{d.label}</div>
                      ))}
                    </div>
                  </div>

                  <div className="so-req-card">
                    <div className="so-req-dot" />
                    <div style={{ flex: 1 }}>
                      <div className="so-req-name">New request · CRED</div>
                      <div className="so-req-sub">Billboard · Apr 1–30, 2026</div>
                      <div className="so-req-btns">
                        <button className="so-req-btn accept" type="button">Accept</button>
                        <button className="so-req-btn review" type="button">Review</button>
                      </div>
                    </div>
                    <div className="so-req-amt">₹45,000</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          PROBLEM — Host perspective
      ════════════════════════════════════════════════ */}
      <section className="problem sec" id="problem" aria-labelledby="prob-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: '560px', marginBottom: 'var(--lumen-space-10)' }}>
            <p className="eyebrow">The current reality</p>
            <h2 className="sec-title" id="prob-hl">This is how space owners get paid today.</h2>
            <p className="sec-desc">
              If you own OOH inventory in India, you already know. Finding buyers, collecting
              payment, and proving your site performed — it all runs on trust, luck, and WhatsApp.
            </p>
          </div>

          <div className="so-prob-cards">

            <ProblemCard
              dataD="1"
              ariaLabel="Problem 1 of 3"
              tag="Problem 1"
              title='"My site in Coimbatore? Call my broker."'
              desc="Thousands of spaces sit empty because national advertisers can't find regional inventory. Excel sheets, personal networks, and broker chains are the only discovery layer."
            >
              <svg viewBox="0 0 260 190" width="260" height="190" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                {/* Map background */}
                <rect x="30" y="20" width="200" height="150" rx="12" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
                {/* Map grid lines */}
                <line x1="30" y1="65" x2="230" y2="65" stroke="#f1f5f9" strokeWidth="1" />
                <line x1="30" y1="110" x2="230" y2="110" stroke="#f1f5f9" strokeWidth="1" />
                <line x1="97" y1="20" x2="97" y2="170" stroke="#f1f5f9" strokeWidth="1" />
                <line x1="163" y1="20" x2="163" y2="170" stroke="#f1f5f9" strokeWidth="1" />
                {/* Faded map roads */}
                <path d="M30 88 Q90 80 130 95 Q170 110 230 100" stroke="#e2e8f0" strokeWidth="2" fill="none" />
                <path d="M100 20 Q115 70 108 110 Q100 150 115 170" stroke="#e2e8f0" strokeWidth="2" fill="none" />
                {/* Grayed-out location pins */}
                <circle cx="80" cy="75" r="9" fill="#e2e8f0" />
                <path d="M80 68 v14" stroke="#cbd5e1" strokeWidth="1.5" />
                <circle cx="150" cy="130" r="9" fill="#e2e8f0" />
                <path d="M150 123 v14" stroke="#cbd5e1" strokeWidth="1.5" />
                {/* Hidden/slashed pin (main) */}
                <circle cx="170" cy="70" r="14" fill="#fee2e2" stroke="#f43f5e" strokeWidth="1.5" />
                <path d="M162 62 L178 78" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M178 62 L162 78" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
                {/* Tooltip: not found */}
                <rect x="114" y="83" width="102" height="28" rx="14" fill="white" stroke="#f43f5e" strokeWidth="1.5" />
                <text x="165" y="101" fill="#f43f5e" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">0 RESULTS FOUND</text>
              </svg>
            </ProblemCard>

            <ProblemCard
              dataD="2"
              ariaLabel="Problem 2 of 3"
              tag="Problem 2"
              title='"Payment after the campaign. Maybe."'
              desc="Advance collection is standard because space owners have no guarantee they'll be paid after the campaign runs. Trust flows one way — and it doesn't always flow back."
            >
              <svg viewBox="0 0 260 190" width="260" height="190" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                {/* Clock face */}
                <circle cx="130" cy="90" r="56" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
                <circle cx="130" cy="90" r="48" fill="#f8fafc" stroke="#f1f5f9" strokeWidth="1" />
                {/* Clock ticks */}
                <line x1="130" y1="46" x2="130" y2="52" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
                <line x1="130" y1="128" x2="130" y2="134" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
                <line x1="86" y1="90" x2="92" y2="90" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
                <line x1="168" y1="90" x2="174" y2="90" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
                {/* Clock hands */}
                <line x1="130" y1="90" x2="130" y2="62" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="130" y1="90" x2="148" y2="92" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="130" cy="90" r="3.5" fill="#64748b" />
                {/* Payment status badge */}
                <rect x="84" y="113" width="92" height="28" rx="14" fill="#fee2e2" stroke="#f43f5e" strokeWidth="1.5" />
                <text x="130" y="131" fill="#f43f5e" fontSize="9.5" fontWeight="700" fontFamily="system-ui,sans-serif" textAnchor="middle">PAYMENT: MAYBE?</text>
                {/* Countdown dots */}
                <circle cx="110" cy="152" r="4" fill="#f43f5e" opacity="1" />
                <circle cx="124" cy="152" r="4" fill="#f43f5e" opacity=".6" />
                <circle cx="138" cy="152" r="4" fill="#f43f5e" opacity=".3" />
                <circle cx="152" cy="152" r="4" fill="#e2e8f0" />
              </svg>
            </ProblemCard>

            <ProblemCard
              dataD="3"
              ariaLabel="Problem 3 of 3"
              tag="Problem 3"
              title='"I track bookings in a notebook."'
              desc="No platform, no calendar, no digital record. Booking conflicts, double-bookings, and missed opportunities are a normal part of running OOH inventory today."
            >
              <svg viewBox="0 0 260 190" width="260" height="190" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                {/* Notebook */}
                <rect x="60" y="18" width="110" height="140" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
                {/* Spine */}
                <rect x="60" y="18" width="16" height="140" rx="4" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
                {/* Spiral dots */}
                <circle cx="68" cy="42" r="3" fill="#cbd5e1" />
                <circle cx="68" cy="62" r="3" fill="#cbd5e1" />
                <circle cx="68" cy="82" r="3" fill="#cbd5e1" />
                <circle cx="68" cy="102" r="3" fill="#cbd5e1" />
                <circle cx="68" cy="122" r="3" fill="#cbd5e1" />
                <circle cx="68" cy="142" r="3" fill="#cbd5e1" />
                {/* Handwritten lines (irregular) */}
                <line x1="84" y1="42" x2="158" y2="40" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="84" y1="54" x2="148" y2="53" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" opacity=".6" />
                <line x1="84" y1="66" x2="155" y2="65" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" opacity=".4" />
                <line x1="84" y1="78" x2="140" y2="77" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" opacity=".5" />
                <line x1="84" y1="90" x2="152" y2="90" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" opacity=".3" />
                {/* Crossed out entry */}
                <line x1="84" y1="108" x2="152" y2="108" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" opacity=".5" />
                <line x1="84" y1="108" x2="152" y2="108" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" opacity=".5" />
                {/* Pencil */}
                <rect x="176" y="80" width="12" height="58" rx="3" fill="#fbbf24" transform="rotate(20 176 80)" />
                <polygon points="191,143 198,150 185,153" fill="#1e293b" />
                <rect x="176" y="80" width="12" height="10" rx="2" fill="#f43f5e" transform="rotate(20 176 80)" />
                {/* Error badge */}
                <circle cx="196" cy="55" r="18" fill="white" stroke="#f43f5e" strokeWidth="1.5" />
                <text x="196" y="63" fill="#f43f5e" fontSize="24" fontWeight="800" fontFamily="system-ui,sans-serif" textAnchor="middle">!</text>
              </svg>
            </ProblemCard>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          HOW LISTING WORKS
      ════════════════════════════════════════════════ */}
      <section className="so-steps sec" id="listing-steps" aria-labelledby="steps-hl">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto' }}>
            <p className="eyebrow">Getting started</p>
            <h2 className="sec-title" id="steps-hl">Three steps to your first booking.</h2>
          </div>

          <div className="so-steps-row">
            <StepCard
              dataD="1"
              num="01"
              title="List your space"
              desc="Add your site details — location, format, dimensions, photos, and pricing. Takes under 10 minutes. Free, always."
            />
            <div className="so-step-conn" aria-hidden="true">
              <div className="so-step-conn-line" />
            </div>
            <StepCard
              dataD="2"
              num="02"
              title="Receive booking requests"
              desc="Advertisers find your space and send booking requests directly. Review the brief, confirm dates, and approve. You stay in control."
            />
            <div className="so-step-conn" aria-hidden="true">
              <div className="so-step-conn-line" />
            </div>
            <StepCard
              dataD="3"
              num="03"
              title="Campaign runs, you get paid"
              desc="Submit verified proof of display and escrow releases your payment automatically. No invoices. No follow-ups. No waiting."
            />
          </div>

          <div className="so-steps-claim reveal">
            <p className="so-steps-claim-text">Payment in escrow before your<br />campaign even starts.</p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          WHAT YOU GET
      ════════════════════════════════════════════════ */}
      <section className="so-value sec" id="value" aria-labelledby="value-hl">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto' }}>
            <p className="eyebrow">Why FlipAd</p>
            <h2 className="sec-title" id="value-hl">Everything you need to monetise your inventory.</h2>
          </div>

          <div className="so-value-grid">
            <ValueCard
              dataD="1"
              icon="ph-check-circle"
              title="Free to list, forever"
              desc="Listing your space on FlipAd costs nothing. No subscription, no upfront fee. FlipAd earns only when a booking completes — our interests are aligned with yours."
            />
            <ValueCard
              dataD="2"
              icon="ph-vault"
              title="Escrow-protected earnings"
              desc="The moment a booking is confirmed, the advertiser's payment moves into escrow. You are not waiting on an invoice or chasing a bank transfer. Submit your proof of play and payment arrives automatically."
            />
            <ValueCard
              dataD="3"
              icon="ph-calendar-dots"
              title="Full booking management"
              desc="A dedicated host dashboard gives you a live view of your availability, incoming requests, confirmed bookings, and payment history. No more notebooks, no more WhatsApp threads."
            />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          DASHBOARD PREVIEW
      ════════════════════════════════════════════════ */}
      <section className="so-dash-preview sec" id="dashboard" aria-labelledby="dashboard-hl">
        <div className="container">
          <div className="so-dash-preview-inner">

            <div className="so-dash-preview-left reveal">
              <p className="eyebrow">Your dashboard</p>
              <h2 className="sec-title" id="dashboard-hl">Everything in one place.</h2>
              <p className="sec-desc">Manage your entire OOH business from a single screen. See what&apos;s booked, what&apos;s available, and what&apos;s been paid — updated in real time.</p>
            </div>

            <div className="reveal" data-d="1">
              <div className="so-browser">
                <div className="so-browser-bar">
                  <div className="so-browser-dot" />
                  <div className="so-browser-dot" />
                  <div className="so-browser-dot" />
                  <div className="so-browser-url">flipad.in/host/my-spaces</div>
                </div>
                <div className="so-browser-body">

                  <div className="so-db-topbar">
                    <div className="so-db-title">My Spaces</div>
                    <div className="so-db-add"><i className="ph ph-plus" style={{ fontSize: '11px' }} /> Add new space</div>
                  </div>

                  <div className="so-db-stats">
                    <div className="so-db-tile">
                      <div className="so-db-tile-val">4</div>
                      <div className="so-db-tile-lbl">Active bookings</div>
                    </div>
                    <div className="so-db-tile">
                      <div className="so-db-tile-val" style={{ fontSize: '17px' }}>₹1,24,000</div>
                      <div className="so-db-tile-lbl">This month&apos;s earnings</div>
                    </div>
                    <div className="so-db-tile">
                      <div className="so-db-tile-val" style={{ color: 'var(--lumen-amber-500)' }}>2</div>
                      <div className="so-db-tile-lbl">Pending requests</div>
                    </div>
                  </div>

                  <div>
                    <div className="so-db-spaces-hd">Your spaces</div>
                    <div className="so-db-spaces-list">
                      <SpaceRow
                        thumbBg="var(--lumen-slate-300)"
                        name="MG Road Billboard — Bengaluru"
                        sub="40×20 ft · Unipole"
                        badge="av"
                        badgeLabel="Available"
                        rate="₹42,000/mo"
                      />
                      <SpaceRow
                        thumbBg="var(--lumen-slate-200)"
                        name="Linking Road Hoarding — Mumbai"
                        sub="60×30 ft · Billboard"
                        badge="bk"
                        badgeLabel="Booked"
                        rate="₹78,000/mo"
                      />
                      <SpaceRow
                        thumbBg="var(--lumen-slate-300)"
                        name="Anna Salai Digital Screen — Chennai"
                        sub="16×9 ft · Digital"
                        badge="pe"
                        badgeLabel="Pending"
                        rate="₹35,000/mo"
                      />
                    </div>
                  </div>

                  <div className="so-db-req">
                    <div className="so-db-req-hd">
                      <div className="so-db-req-lbl">
                        <div className="so-db-req-pulse" />
                        <div className="so-db-req-title">New booking request · CRED</div>
                      </div>
                      <div className="so-db-req-amt">₹45,000</div>
                    </div>
                    <div className="so-db-req-meta">Billboard · MG Road, Bengaluru &nbsp;·&nbsp; Apr 1–30, 2026</div>
                    <div className="so-db-req-btns">
                      <button className="so-db-req-btn accept" type="button">Accept booking</button>
                      <button className="so-db-req-btn review" type="button">Review brief</button>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          ESCROW — Host perspective
      ════════════════════════════════════════════════ */}
      <section className="escrow sec" id="escrow" aria-labelledby="escrow-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: '620px' }}>
            <p className="eyebrow">How money moves</p>
            <h2 className="sec-title" id="escrow-hl" style={{ color: '#fff' }}>
              Your earnings are secured the moment a booking confirms.
            </h2>
            <p className="sec-desc" style={{ color: 'var(--lumen-slate-400)' }}>
              FlipAd holds the payment in escrow from day one. You don&apos;t need to chase it,
              invoice for it, or trust a stranger. Submit your proof of play and it arrives automatically.
            </p>
          </div>

          <div className="esc-scroll-hint" aria-hidden="true">
            <i className="ph ph-arrows-left-right" style={{ fontSize: '14px' }} />
            Scroll to see full payment flow
          </div>

          <div
            className="esc-diag reveal"
            id="esc-diag"
            ref={escDiagRef}
            role="img"
            aria-label="How FlipAd escrow works: advertiser books, payment locked, campaign runs, proof submitted, funds released"
          >
            <div className="esc-stepper">
              <EscrowStep n={1} icon="ph-user-circle" title="Advertiser Books" sub={<>Selects space<br />&amp; dates</>} />
              <EscrowConnector n={1} color="blue" label="₹ PAYMENT" />
              <EscrowStep n={2} icon="ph-vault" title="Funds Locked" sub={<>Held by FlipAd<br />Protected · Escrow</>} isEscrow />
              <EscrowConnector n={2} color="blue" label="CAMPAIGN START" />
              <EscrowStep n={3} icon="ph-megaphone" title="Campaign Runs" sub={<>Space goes live<br />Both sides active</>} />
              <EscrowConnector n={3} color="blue" label="PROOF" />
              <EscrowStep n={4} icon="ph-camera" title="Proof Submitted" sub={<>Timestamped<br />Verified by FlipAd</>} />
              <EscrowConnector n={4} color="green" label="ON PROOF" />
              <EscrowStep n={5} icon="ph-check-circle" title="Funds Released" sub={<>Space owner paid<br />Campaign closed</>} />
            </div>
          </div>

          <div className="escrow-guarantees">
            <div className="escrow-g adv reveal" data-d="1">
              <div className="escrow-g-icon adv" aria-hidden="true"><i className="ph ph-lock-laminated" /></div>
              <div className="escrow-g-title">Payment locked before you lift a finger.</div>
              <div className="escrow-g-desc">
                The advertiser&apos;s payment moves to escrow the moment your booking is confirmed.
                You don&apos;t need to ask for it, chase it, or invoice anyone. It&apos;s already waiting —
                secured from day one, regardless of how long the campaign runs.
              </div>
            </div>

            <div className="escrow-g host reveal" data-d="2">
              <div className="escrow-g-icon host" aria-hidden="true"><i className="ph ph-currency-inr" /></div>
              <div className="escrow-g-title">Submit proof. Receive payment. Done.</div>
              <div className="escrow-g-desc">
                Upload a timestamped photo of your campaign running. FlipAd verifies it and
                releases the funds automatically. No disputes, no delays, no middlemen
                between you and your earnings.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FAQ
      ════════════════════════════════════════════════ */}
      <section className="so-faq sec" id="faq" aria-labelledby="faq-hl">
        <div className="container">
          <div className="faq-layout">

            <div className="faq-main">
              <div className="sec-header reveal" style={{ maxWidth: '560px', marginBottom: 'var(--lumen-space-10)' }}>
                <p className="eyebrow">Common questions</p>
                <h2 className="sec-title" id="faq-hl">Everything you need to know about listing on FlipAd.</h2>
              </div>

              <div className="so-faq-list reveal">
                {FAQS.map((f, i) => (
                  <FaqItem
                    key={f.q}
                    q={f.q}
                    a={f.a}
                    isOpen={openFaqIndex === i}
                    onToggle={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  />
                ))}
              </div>
            </div>

            <aside className="faq-side reveal" data-d="2">
              <div className="faq-card">
                <span className="faq-card-badge"><i className="ph-fill ph-circle" aria-hidden="true" />Avg. reply under 2 hours</span>
                <h3 className="faq-card-title">Still have a question?</h3>
                <p className="faq-card-desc">Real people, not bots. Reach out and we&apos;ll walk you through listing, payouts, or a specific site.</p>
                <div className="faq-card-actions">
                  <a href="mailto:support@flipad.in" className="lmn-btn secondary sm"><i className="ph ph-envelope-simple" aria-hidden="true" />Email support</a>
                  <a href="#" className="lmn-btn primary sm" onClick={(e) => e.preventDefault()}><i className="ph ph-whatsapp-logo" aria-hidden="true" />Chat on WhatsApp</a>
                </div>
              </div>
              <div className="faq-trust">
                <div className="faq-trust-item"><i className="ph ph-currency-inr" aria-hidden="true" /><span><strong>Free</strong>&nbsp;to list, always</span></div>
                <div className="faq-trust-item"><i className="ph ph-shield-check" aria-hidden="true" /><span><strong>100%</strong>&nbsp;escrow-protected payouts</span></div>
                <div className="faq-trust-item"><i className="ph ph-buildings" aria-hidden="true" /><span><strong>23</strong>&nbsp;cities covered</span></div>
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CLOSING CTA
      ════════════════════════════════════════════════ */}
      <section className="so-cta sec" id="cta" aria-labelledby="cta-hl">
        <div className="container">
          <div className="reveal">
            <p className="eyebrow">Get started</p>
            <h2 className="so-cta-hl" id="cta-hl">Ready to put your inventory to work?</h2>
            <p className="so-cta-sub">List your first space in under 10 minutes. Free, always.</p>
            <div className="so-cta-btns">
              <a
                href={appUrl("/auth")}
                target="_blank"
                rel="noopener noreferrer"
                className="lmn-btn lg"
                style={{ background: '#fff', borderColor: '#fff', color: 'var(--b900)' }}
              >
                List your space <i className="ph ph-arrow-right" aria-hidden="true" />
              </a>
            </div>
            <p className="so-cta-link">
              Have questions?{' '}
              <a href="mailto:support@flipad.in" style={{ color: 'color-mix(in srgb, #fff 60%, transparent)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>Contact us →</a>
            </p>
            <p className="so-cta-link" style={{ marginTop: 'var(--lumen-space-3)' }}>
              Looking to advertise instead?{' '}
              <button
                type="button"
                style={{
                  color: 'color-mix(in srgb, #fff 60%, transparent)',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  cursor: 'pointer',
                }}
                onClick={openPlayStore}
              >
                Browse inventory →
              </button>
            </p>
          </div>
        </div>
      </section>

      <Footer listASpaceHref="#listing-steps" escrowHref="#escrow" />
    </>
  );
}
